import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowUpRight, Briefcase, CalendarDays, CheckCircle2, Hourglass, UserX } from "lucide-react";
import { PageHeader, StaffBadge, ProgressLine } from "@/components/atlas/Shared";
import { GoldButton } from "@/components/atlas/Fields";
import { EventCalendar } from "@/components/dashboard/EventCalendar";
import { OffsiteAttempts } from "@/components/dashboard/OffsiteAttempts";
import { useFetch } from "@/lib/positions";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { fmtShort, fmtDate } from "@/lib/format";

const HERO = "/dashboard-hero.jpg";

const Kpi = ({ label, value, accent, testid, delay, icon: Icon }) => (
  <div className="card-lux p-5 rise relative overflow-hidden" style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
    {accent && <div className="absolute top-0 left-6 right-6 h-[2px] rounded-full bg-[#C5A059]" />}
    <Icon className="w-5 h-5 text-[#C5A059] mb-4" strokeWidth={1.5} />
    <div className="mono text-3xl font-medium">{value ?? "—"}</div>
    <div className="text-xs text-neutral-500 mt-1">{label}</div>
  </div>
);

const NextEvent = ({ e }) => {
  const { t, lang } = useLang();
  return (
    <Link to={`/admin/events/${e.id}`} data-testid="next-event-card" className="dark-zone press group relative block overflow-hidden rounded-2xl border border-white/[0.08] mb-10 rise">
      <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-55" style={{ transition: "opacity 0.4s" }} />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/20" />
      <div className="relative p-6 md:p-8 max-w-xl">
        <div className="eyebrow text-[#D9B872]">{t("dash.next")}</div>
        <h2 className="font-display text-3xl md:text-4xl mt-2 text-white">{e.name}</h2>
        <p className="text-sm text-white/60 mt-2 capitalize">{fmtDate(e.date, lang)} · {e.location}</p>
        <div className="mt-6 text-xs text-white/70 mb-2"><span className="mono text-white">{e.stats.filled} / {e.stats.required}</span> {t("dash.filled")}</div>
        <ProgressLine filled={e.stats.filled} required={e.stats.required} />
      </div>
      <ArrowUpRight className="absolute top-6 right-6 w-5 h-5 text-white/50 group-hover:text-[#C5A059]" />
    </Link>
  );
};

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data } = useFetch("/dashboard");
  const { data: events } = useFetch("/events");
  const attention = (data?.events || []).filter((e) => e.stats.unfilled > 0).slice(0, 6);

  return (
    <div>
      <PageHeader eyebrow={t("dash.hello")} title={<>{t("dash.greet")}, <span className="text-[#D9B872]">{user.name.split(" ")[0]}</span></>}
        actions={<GoldButton data-testid="dashboard-new-event-btn" onClick={() => navigate("/admin/events/new")}><Plus className="w-4 h-4" />{t("ev.new")}</GoldButton>} />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Kpi icon={CalendarDays} label={t("dash.upcoming")} value={data?.upcoming_events} testid="kpi-upcoming" delay={0} />
        <Kpi icon={Briefcase} label={t("dash.needsStaff")} value={data?.needs_staff} testid="kpi-needs-staff" delay={60} />
        <Kpi icon={CheckCircle2} label={t("dash.full")} value={data?.fully_staffed} testid="kpi-fully-staffed" delay={120} />
        <Kpi icon={Hourglass} label={t("dash.pending")} value={data?.pending_confirmations} testid="kpi-pending" delay={180} />
        <Kpi icon={UserX} label={t("dash.unfilled")} value={data?.unfilled_positions} accent testid="kpi-unfilled" delay={240} />
      </div>
      {data?.events?.find((e) => e.status === "published") && <NextEvent e={data.events.find((e) => e.status === "published")} />}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-8">
        <EventCalendar events={events} />
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl">{t("dash.attention")}</h2>
          </div>
          <div className="space-y-3" data-testid="attention-list">
            {data && attention.length === 0 && <p className="text-sm text-neutral-500 card-lux p-6">{t("dash.allGood")}</p>}
            {attention.map((e, i) => (
              <Link key={e.id} to={`/admin/events/${e.id}`} data-testid={`attention-event-${e.id}`}
                className="press block card-lux p-5 hover:border-[#C5A059]/50 rise group" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="eyebrow capitalize">{fmtShort(e.date, lang)} · {e.location}</div>
                    <div className="font-medium mt-1 truncate">{e.name}</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-[#C5A059] shrink-0" />
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <div className="flex-1"><ProgressLine filled={e.stats.filled} required={e.stats.required} /></div>
                  <StaffBadge status={e.stats.staffing_status} />
                </div>
                <div className="mt-2 text-xs text-neutral-500">{e.stats.unfilled} {t("b.remaining")}</div>
              </Link>
            ))}
          </div>
          <OffsiteAttempts />
        </div>
      </div>
    </div>
  );
}
